'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/contacts-stakeholders.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/contacts-stakeholders#contacts-on-jobs-and-appointments)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Building2,
  MapPin,
  User,
  Plus,
  Save,
  Loader2,
  Search,
  ChevronDown,
  Check,
  Phone,
  Mail,
  X,
  AlertTriangle,
  CheckCircle,
  CheckCircle2,
  XCircle,
  ShieldAlert,
  ShieldCheck,
  Star,
  MessageCircle,
  ExternalLink,
  Info,
  UserPlus,
  Pencil,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import GoogleMapsPicker from '@/components/maps/GoogleMapsPicker';
import QuickContactModal, { type QuickContactResult } from '@/components/contacts/QuickContactModal';
import DetailHoverCard from '@/components/shared/DetailHoverCard';
import { buildStakeholderContactWorkItem } from '@/components/contacts/stakeholderWorkItemAdapter';
import useKeyboardSelectableList from '@/hooks/useKeyboardSelectableList';
import dynamic from 'next/dynamic';

// Lazy-load the detail modal so importing the lookup doesn't pull the entire
// work-item bundle into every form that uses it.
const WorkItemDetailModal = dynamic(() => import('@/components/WorkItemDetailModal'), { ssr: false });
import {
  calculateContactAccountStanding,
  formatContactAccountStatusLabel,
  formatContactPaymentTerms,
  isSupplierStatus,
  isSupplierWarningStatus,
  isSupplierRestrictedStatus,
  formatSupplierStatusLabel,
} from '@/lib/contacts/accountStanding';

export interface UnifiedLookupContact {
  id: number;
  work_item_id?: string;
  company_name: string | null;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  contact_type: string;
  account_status?: string | null;
  is_active?: boolean | null;
  payment_terms?: string | number | null;
  payment_terms_days?: string | number | null;
  credit_limit?: number | string | null;
  current_balance?: number | string | null;
  currency?: string | null;
}

export interface UnifiedLookupLocation {
  id: number;
  work_item_id?: string;
  contact_id: number;
  contact_work_item_id?: string | null;
  location_name: string;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  latitude?: number | null;
  longitude?: number | null;
  contact_person: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  is_primary: boolean;
}

export interface UnifiedLookupContactPerson {
  id: number;
  work_item_id?: string;
  contact_id: number;
  contact_work_item_id?: string | null;
  first_name: string;
  last_name: string;
  job_title: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  is_primary: boolean;
  default_location_id: number | null;
  default_location_work_item_id?: string | null;
  default_location_name: string | null;
  contact_company_name: string | null;
}

export interface UnifiedLookupSearchResult {
  type: 'contact' | 'location' | 'person';
  id: number;
  work_item_id?: string;
  stakeholder_entity_kind?: 'contact' | 'location' | 'person';
  contact_id: number;
  contact_work_item_id?: string | null;
  contact_name: string;
  contact_company_name: string | null;
  contact_type: string;
  location_id: number | null;
  location_work_item_id?: string | null;
  location_name: string | null;
  location_address: string | null;
  location_city: string | null;
  person_id: number | null;
  person_work_item_id?: string | null;
  person_name: string | null;
  person_job_title: string | null;
  person_email: string | null;
  person_phone: string | null;
  display_title: string;
  display_subtitle: string;
}

type SearchMode = 'contact' | 'location' | 'person';

const DEFAULT_CONTACT_TYPES = ['customer', 'supplier'] as const;

export interface UnifiedContactLookupProps {
  contacts: UnifiedLookupContact[];
  locations: UnifiedLookupLocation[];
  contactPeople: UnifiedLookupContactPerson[];
  selectedContactId: number | null;
  selectedContactWorkItemId?: string | null;
  selectedLocationId: number | null;
  selectedLocationWorkItemId?: string | null;
  selectedContactPersonId: number | null;
  selectedContactPersonWorkItemId?: string | null;
  selectedContactPersonName: string;
  onContactChange: (id: number | null, contactName?: string) => void;
  onLocationChange: (id: number | null) => void;
  onContactPersonChange: (id: number | null, name: string) => void;
  onUnifiedSelect: (result: UnifiedLookupSearchResult) => void;
  onLocationCreated: (newLocation: UnifiedLookupLocation) => void;
  onPersonCreated: (newPerson: UnifiedLookupContactPerson) => void;
  loadingContacts: boolean;
  loadingLocations: boolean;
  loadingPeople: boolean;
  isDark: boolean;
  contactLabel?: string;
  contactPlaceholder?: string;
  contactSearchPlaceholder?: string;
  /** Extra query params merged into the /api/v1/contacts search call —
   *  e.g. {tenant_linked:'true'} for tenant-bound pickers. */
  contactSearchExtraParams?: Record<string, string>;
  /** Render only the contact combobox — hides the mode tabs, card view and
   *  the location/person sections. For compact single-purpose pickers. */
  contactOnly?: boolean;
  locationLabel?: string;
  personLabel?: string;
  contactTypes?: readonly string[];
  documentType?: string;
  documentTotal?: number;
  onViewStakeholder?: () => void;
  onOnboardStakeholder?: () => void;
  /** Fired when the per-contact edit (pencil) icon is clicked. When omitted the
   *  lookup opens an internal WorkItemDetailModal (StakeholderOverviewTab) for
   *  the contact. Provide this to override with custom navigation (e.g.
   *  `onOpenItem` in unified views). */
  onEditContact?: (contact: UnifiedLookupContact) => void;
  /** Fired after a contact is created via the inline QuickContactModal.
   *  Parents can use this to auto-open the stakeholder overview for the
   *  newly created supplier (e.g. to review payment terms). */
  onQuickContactCreated?: (result: QuickContactResult) => void;
  /** Risk flags for the selected contact — displayed as a warning badge */
  contactRiskFlags?: Array<{ id: string; severity: 'low' | 'medium' | 'high' | 'critical'; title: string }>;
  /** Increment this counter to imperatively open the inline "Add Location"
   *  form (e.g. from a parent submit handler that detects the selected
   *  location has no GPS coordinates). The form only opens when a contact
   *  is already selected. */
  addLocationSignal?: number;
  /** Opt-in multi-person mode. When > 1 and onContactPersonsChange is provided,
   *  the person picker renders an ordered selection (index 0 = primary person)
   *  capped at this many people. The parent should keep selectedContactPersonId
   *  in sync with the first selected id so single-person fields stay current. */
  maxContactPeople?: number;
  /** Ordered selected person ids in multi mode; index 0 is the primary person. */
  selectedContactPersonIds?: number[];
  /** Fired with the ordered ids and parallel display names whenever the
   *  multi-person selection changes (add/remove). */
  onContactPersonsChange?: (ids: number[], names: string[]) => void;
}

export default function UnifiedContactLookup({
  contacts,
  locations,
  contactPeople,
  selectedContactId,
  selectedContactWorkItemId = null,
  selectedLocationId,
  selectedLocationWorkItemId = null,
  selectedContactPersonId,
  selectedContactPersonWorkItemId = null,
  selectedContactPersonName,
  onContactChange,
  onLocationChange,
  onContactPersonChange,
  onUnifiedSelect,
  onLocationCreated,
  onPersonCreated,
  loadingContacts,
  loadingLocations,
  loadingPeople,
  isDark,
  contactLabel = 'Customer/Contact *',
  contactPlaceholder = 'Select customer...',
  contactTypes = DEFAULT_CONTACT_TYPES,
  contactSearchPlaceholder = 'Search contacts...',
  contactSearchExtraParams,
  contactOnly = false,
  locationLabel = 'Location *',
  personLabel = 'Contact Person',
  documentType = 'job',
  documentTotal = 0,
  onViewStakeholder,
  onOnboardStakeholder,
  onEditContact,
  onQuickContactCreated,
  contactRiskFlags = [],
  addLocationSignal = 0,
  maxContactPeople = 1,
  selectedContactPersonIds,
  onContactPersonsChange,
}: UnifiedContactLookupProps) {
  const [searchMode, setSearchMode] = useState<SearchMode>('contact');
  const [contactSearch, setContactSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [personSearch, setPersonSearch] = useState('');
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [showQuickContactModal, setShowQuickContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState<UnifiedLookupContact | null>(null);
  const [isLocationSearchOpen, setIsLocationSearchOpen] = useState(false);
  const [isPersonSearchOpen, setIsPersonSearchOpen] = useState(false);
  const [locationSearchResults, setLocationSearchResults] = useState<UnifiedLookupSearchResult[]>([]);
  const [personSearchResults, setPersonSearchResults] = useState<UnifiedLookupSearchResult[]>([]);
  const [loadingLocationSearch, setLoadingLocationSearch] = useState(false);
  const [loadingPersonSearch, setLoadingPersonSearch] = useState(false);
  const [lastLocationResult, setLastLocationResult] = useState<UnifiedLookupSearchResult | null>(null);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [savingPerson, setSavingPerson] = useState(false);
  const [newLocation, setNewLocation] = useState({
    location_name: '',
    location_type: 'service_location',
    address_line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'South Africa',
    latitude: null as number | null,
    longitude: null as number | null,
    radius_meters: 100,
    is_primary: false,
  });
  const [newPerson, setNewPerson] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    job_title: '',
    is_primary: false,
  });

  // Imperatively open the inline Add Location form when the parent increments
  // `addLocationSignal` (e.g. after a blocking prompt about missing GPS coords).
  const prevAddLocationSignalRef = useRef(addLocationSignal);
  useEffect(() => {
    if (addLocationSignal !== prevAddLocationSignalRef.current) {
      prevAddLocationSignalRef.current = addLocationSignal;
      if (addLocationSignal > 0 && selectedContactId) {
        setShowAddLocation(true);
      }
    }
  }, [addLocationSignal, selectedContactId]);

  const [contactSearchResults, setContactSearchResults] = useState<UnifiedLookupContact[]>([]);
  const [loadingContactSearch, setLoadingContactSearch] = useState(false);
  const [viewMode, setViewMode] = useState<'card' | 'search'>('search');
  const prevContactIdRef = useRef<number | null>(null);

  const locationTypes = ['office', 'warehouse', 'billing', 'shipping', 'service_location', 'job_site', 'residential', 'factory', 'depot'];
  const contactRef = useRef<HTMLDivElement>(null);
  const locationSearchRef = useRef<HTMLDivElement>(null);
  const personSearchRef = useRef<HTMLDivElement>(null);
  const latestContactSearchRequestId = useRef(0);
  const latestLocationSearchRequestId = useRef(0);
  const latestPersonSearchRequestId = useRef(0);
  const contactTypesQueryValue = contactTypes.length > 0
    ? contactTypes.join(',')
    : DEFAULT_CONTACT_TYPES.join(',');

  const getAuthToken = useCallback(() => authService.getTokens()?.access_token || '', []);

  // Latest extra search params — read inside performContactSearch without
  // destabilising its useCallback identity (would retrigger the search effect).
  const contactSearchExtraParamsRef = useRef(contactSearchExtraParams);
  contactSearchExtraParamsRef.current = contactSearchExtraParams;

  const handleMapLocationSelect = (lat: string, lng: string, address?: string) => {
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    const parsed = { street: '', city: '', state: '', postal_code: '', country: 'South Africa' };

    if (address) {
      const parts = address.split(',').map((part) => part.trim());
      if (parts.length >= 5) {
        parsed.street = parts[0];
        parsed.city = parts[2];
        parsed.postal_code = parts[3];
        parsed.country = parts[4] || 'South Africa';
      } else if (parts.length >= 4) {
        parsed.street = parts[0];
        parsed.city = parts[1];
        parsed.postal_code = parts[2];
        parsed.country = parts[3] || 'South Africa';
      } else if (parts.length >= 2) {
        parsed.street = parts[0];
        parsed.city = parts[1];
      }

      if (parsed.country === 'South Africa' || address.includes('South Africa')) {
        const postalCode = parseInt(parsed.postal_code, 10);
        if (!Number.isNaN(postalCode)) {
          if ((postalCode >= 0 && postalCode <= 299) || (postalCode >= 1000 && postalCode <= 2199)) parsed.state = 'Gauteng';
          else if (postalCode >= 300 && postalCode <= 499) parsed.state = 'North West';
          else if ((postalCode >= 500 && postalCode <= 699) || (postalCode >= 9300 && postalCode <= 9999)) parsed.state = 'Free State';
          else if ((postalCode >= 700 && postalCode <= 999) || (postalCode >= 8300 && postalCode <= 8999)) parsed.state = 'Northern Cape';
          else if (postalCode >= 2500 && postalCode <= 2999) parsed.state = 'Mpumalanga';
          else if (postalCode >= 3000 && postalCode <= 4499) parsed.state = 'KwaZulu-Natal';
          else if (postalCode >= 4500 && postalCode <= 6499) parsed.state = 'Eastern Cape';
          else if (postalCode >= 6500 && postalCode <= 8299) parsed.state = 'Western Cape';
        }
      }
    }

    setNewLocation((prev) => ({
      ...prev,
      latitude: latNum,
      longitude: lngNum,
      address_line1: parsed.street || address || prev.address_line1,
      city: parsed.city || prev.city,
      state: parsed.state || prev.state,
      postal_code: parsed.postal_code || prev.postal_code,
      country: parsed.country || prev.country,
    }));
  };

  const handleSaveNewLocation = async () => {
    if (!selectedContactId) return;
    if (!newLocation.location_name.trim()) {
      notify.error('Location name is required');
      return;
    }
    if (!newLocation.latitude || !newLocation.longitude) {
      notify.error('GPS coordinates are required. Pick a location on the map or use the search.');
      return;
    }

    setSavingLocation(true);
    try {
      const response = await fetch(`/api/v1/contacts/${selectedContactId}/locations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newLocation),
      });

      if (response.ok) {
        const result = await response.json();
        notify.success('Location created');
        setShowAddLocation(false);
        setNewLocation({
          location_name: '',
          location_type: 'service_location',
          address_line1: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'South Africa',
          latitude: null,
          longitude: null,
          radius_meters: 100,
          is_primary: false,
        });
        if (result.data) {
          onLocationCreated(result.data);
        }
      } else {
        const data = await response.json();
        notify.error(data.error || 'Failed to create location');
      }
    } catch (error) {
      console.error('Error creating location:', error);
      notify.error('Failed to create location');
    } finally {
      setSavingLocation(false);
    }
  };

  const handleSaveNewPerson = async () => {
    if (!selectedContactId) return;
    if (!newPerson.first_name.trim() || !newPerson.last_name.trim()) {
      notify.error('First name and last name are required');
      return;
    }

    setSavingPerson(true);
    try {
      const response = await fetch(`/api/v1/contacts/${selectedContactId}/people`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newPerson),
      });

      if (response.ok) {
        const result = await response.json();
        notify.success('Contact person created');
        setShowAddPerson(false);
        setNewPerson({ first_name: '', last_name: '', email: '', phone: '', mobile: '', job_title: '', is_primary: false });
        if (result.data) {
          onPersonCreated(result.data);
        }
      } else {
        const data = await response.json();
        notify.error(data.error || 'Failed to create person');
      }
    } catch (error) {
      console.error('Error creating person:', error);
      notify.error('Failed to create person');
    } finally {
      setSavingPerson(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: PointerEvent) => {
      if (contactRef.current && !contactRef.current.contains(e.target as Node)) {
        setIsContactOpen(false);
      }
      if (locationSearchRef.current && !locationSearchRef.current.contains(e.target as Node)) {
        setIsLocationSearchOpen(false);
      }
      if (personSearchRef.current && !personSearchRef.current.contains(e.target as Node)) {
        setIsPersonSearchOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleClickOutside, true);
    return () => document.removeEventListener('pointerdown', handleClickOutside, true);
  }, []);

  const performContactSearch = useCallback(async (search: string) => {
    const normalizedSearch = search.trim();
    if (normalizedSearch.length < 2) {
      latestContactSearchRequestId.current += 1;
      setContactSearchResults([]);
      setLoadingContactSearch(false);
      return;
    }

    const requestId = ++latestContactSearchRequestId.current;

    try {
      setLoadingContactSearch(true);
      const params = new URLSearchParams({
        search: normalizedSearch,
        is_active: 'true',
        limit: '50',
        contact_types: contactTypesQueryValue,
        ...(contactSearchExtraParamsRef.current || {}),
      });
      const response = await fetch(`/api/v1/contacts?${params}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (requestId !== latestContactSearchRequestId.current) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const results: UnifiedLookupContact[] = (data.data || []).map((c: Record<string, unknown>) => ({
          id: c.id as number,
          company_name: (c.company_name as string | null) || null,
          contact_person: (c.contact_person as string | null) || null,
          email: (c.email as string | null) || null,
          phone: (c.phone as string | null) || (c.mobile as string | null) || null,
          contact_type: c.contact_type as string,
          account_status: (c.account_status as string | null) ?? null,
          is_active: (c.is_active as boolean | null) ?? null,
          payment_terms: c.payment_terms ?? null,
          payment_terms_days: c.payment_terms_days ?? null,
          credit_limit: c.credit_limit ?? null,
          current_balance: c.current_balance ?? null,
          currency: (c.currency as string | null) ?? null,
        }));
        setContactSearchResults(results);
      } else {
        setContactSearchResults([]);
      }
    } catch (error) {
      if (requestId !== latestContactSearchRequestId.current) {
        return;
      }
      console.error('Error searching contacts:', error);
      setContactSearchResults([]);
    } finally {
      if (requestId === latestContactSearchRequestId.current) {
        setLoadingContactSearch(false);
      }
    }
  }, [contactTypesQueryValue, getAuthToken]);

  const performLocationSearch = useCallback(async (search: string) => {
    const normalizedSearch = search.trim();
    if (normalizedSearch.length < 2) {
      latestLocationSearchRequestId.current += 1;
      setLocationSearchResults([]);
      setLoadingLocationSearch(false);
      return;
    }

    const requestId = ++latestLocationSearchRequestId.current;

    try {
      setLoadingLocationSearch(true);
      const response = await fetch(
        `/api/v1/contacts/locations/search?search=${encodeURIComponent(normalizedSearch)}&limit=30`,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } },
      );
      if (requestId !== latestLocationSearchRequestId.current) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const locations = (data.data || []).map((loc: Record<string, unknown>) => ({
          type: 'location' as const,
          id: loc.id as number,
          contact_id: loc.contact_id as number,
          contact_name: (loc.contact_name as string) || '',
          contact_company_name: (loc.contact_company as string) || null,
          contact_type: '',
          location_id: loc.id as number,
          location_name: (loc.location_name as string) || '',
          location_address: [loc.address_line1, loc.address_line2].filter(Boolean).join(', ') || null,
          location_city: (loc.city as string) || null,
          person_id: null,
          person_name: null,
          person_job_title: null,
          person_email: null,
          person_phone: null,
          display_title: (loc.location_name as string) || '',
          display_subtitle: [loc.address_line1, loc.city].filter(Boolean).join(', ') || '',
        }));
        setLocationSearchResults(locations);
      } else {
        setLocationSearchResults([]);
      }
    } catch (error) {
      if (requestId !== latestLocationSearchRequestId.current) {
        return;
      }

      console.error('Error in location search:', error);
      setLocationSearchResults([]);
    } finally {
      if (requestId === latestLocationSearchRequestId.current) {
        setLoadingLocationSearch(false);
      }
    }
  }, [getAuthToken]);

  const performPersonSearch = useCallback(async (search: string) => {
    const normalizedSearch = search.trim();
    if (normalizedSearch.length < 2) {
      latestPersonSearchRequestId.current += 1;
      setPersonSearchResults([]);
      setLoadingPersonSearch(false);
      return;
    }

    const requestId = ++latestPersonSearchRequestId.current;

    try {
      setLoadingPersonSearch(true);
      const response = await fetch(
        `/api/v1/contacts/search/unified/?search=${encodeURIComponent(normalizedSearch)}&contact_types=customer,supplier&types=person&limit=30`,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } },
      );
      if (requestId !== latestPersonSearchRequestId.current) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setPersonSearchResults((data.data || []).filter((result: UnifiedLookupSearchResult) => result.type === 'person'));
      } else {
        setPersonSearchResults([]);
      }
    } catch (error) {
      if (requestId !== latestPersonSearchRequestId.current) {
        return;
      }

      console.error('Error in person search:', error);
      setPersonSearchResults([]);
    } finally {
      if (requestId === latestPersonSearchRequestId.current) {
        setLoadingPersonSearch(false);
      }
    }
  }, [getAuthToken]);

  useEffect(() => {
    const normalizedSearch = locationSearch.trim();

    if (searchMode === 'location' && normalizedSearch.length >= 2) {
      void performLocationSearch(normalizedSearch);
    } else if (searchMode === 'location') {
      latestLocationSearchRequestId.current += 1;
      setLocationSearchResults([]);
      setLoadingLocationSearch(false);
    }
  }, [locationSearch, performLocationSearch, searchMode]);

  useEffect(() => {
    const normalizedSearch = personSearch.trim();

    if (searchMode === 'person' && normalizedSearch.length >= 2) {
      void performPersonSearch(normalizedSearch);
    } else if (searchMode === 'person') {
      latestPersonSearchRequestId.current += 1;
      setPersonSearchResults([]);
      setLoadingPersonSearch(false);
    }
  }, [performPersonSearch, personSearch, searchMode]);

  useEffect(() => {
    const normalizedSearch = contactSearch.trim();

    if (searchMode === 'contact' && normalizedSearch.length >= 2) {
      const timer = setTimeout(() => {
        void performContactSearch(normalizedSearch);
      }, 300);
      return () => clearTimeout(timer);
    } else if (searchMode === 'contact') {
      latestContactSearchRequestId.current += 1;
      setContactSearchResults([]);
      setLoadingContactSearch(false);
    }
  }, [contactSearch, performContactSearch, searchMode]);

  // Use server-side results when a search query is active, otherwise show pre-loaded contacts
  const filteredContacts = contactSearch.trim().length >= 2
    ? contactSearchResults
    : contacts.filter((contact) => {
        const name = `${contact.company_name || ''} ${contact.contact_person || ''} ${contact.email || ''}`.toLowerCase();
        return name.includes(contactSearch.toLowerCase());
      });

  const summaryContactLabel = contactLabel.replace(/\s*\*\s*$/, '') || 'Contact';
  const normalizedSelectedContactPersonName = selectedContactPersonName.trim().toLowerCase();

  const selectedContact = contacts.find((contact) => (
    (selectedContactWorkItemId && contact.work_item_id === selectedContactWorkItemId)
    || (selectedContactId != null && Number(contact.id) === Number(selectedContactId))
  ));
  const selectedLocation = locations.find((location) => (
    (selectedLocationWorkItemId && location.work_item_id === selectedLocationWorkItemId)
    || (selectedLocationId != null && Number(location.id) === Number(selectedLocationId))
  ));
  const selectedPerson = contactPeople.find((person) => (
    (selectedContactPersonWorkItemId && person.work_item_id === selectedContactPersonWorkItemId)
    || (selectedContactPersonId != null && Number(person.id) === Number(selectedContactPersonId))
  ))
    || (normalizedSelectedContactPersonName
      ? contactPeople.find((person) => `${person.first_name} ${person.last_name}`.trim().toLowerCase() === normalizedSelectedContactPersonName)
      : undefined);
  const effectiveSelectedContactId = selectedContactId ?? selectedContact?.id ?? null;
  const effectiveSelectedLocationId = selectedLocationId ?? selectedLocation?.id ?? null;
  const effectiveSelectedContactPersonId = selectedContactPersonId ?? selectedPerson?.id ?? null;

  // Auto-switch to card view when a contact is selected, search view when cleared
  useEffect(() => {
    const currentId = effectiveSelectedContactId;
    if (currentId !== prevContactIdRef.current) {
      setViewMode(currentId != null ? 'card' : 'search');
      prevContactIdRef.current = currentId;
    }
  }, [effectiveSelectedContactId]);

  // Filter contact people by the selected contact to ensure the dropdown
  // only shows people belonging to the selected contact.
  const filteredContactPeople = useMemo(() => {
    if (effectiveSelectedContactId == null) return [];
    return contactPeople.filter((person) => Number(person.contact_id) === Number(effectiveSelectedContactId));
  }, [contactPeople, effectiveSelectedContactId]);

  // Multi-person selection (opt-in): ordered ids where index 0 is the primary
  // person persisted in work_items.contact_person_id. Falls back to the primary
  // id prop so single-person callers still render correctly.
  const maxPeople = Math.max(1, maxContactPeople);
  const isMultiPerson = Boolean(onContactPersonsChange) && maxPeople > 1;
  const selectedPersonIds = useMemo(() => {
    const source = isMultiPerson && selectedContactPersonIds && selectedContactPersonIds.length > 0
      ? selectedContactPersonIds
      : (effectiveSelectedContactPersonId != null ? [effectiveSelectedContactPersonId] : []);
    const deduped: number[] = [];
    for (const raw of source) {
      const id = Number(raw);
      if (!Number.isFinite(id) || deduped.includes(id)) continue;
      deduped.push(id);
      if (deduped.length >= maxPeople) break;
    }
    return deduped;
  }, [isMultiPerson, selectedContactPersonIds, effectiveSelectedContactPersonId, maxPeople]);

  const selectedPeople = useMemo(() => (
    selectedPersonIds
      .map((id) => contactPeople.find((person) => Number(person.id) === Number(id)))
      .filter((person): person is UnifiedLookupContactPerson => Boolean(person))
  ), [contactPeople, selectedPersonIds]);

  const emitPersonsChange = useCallback((ids: number[]) => {
    if (!onContactPersonsChange) return;
    const deduped: number[] = [];
    for (const raw of ids) {
      const id = Number(raw);
      if (!Number.isFinite(id) || deduped.includes(id)) continue;
      deduped.push(id);
      if (deduped.length >= maxPeople) break;
    }
    onContactPersonsChange(
      deduped,
      deduped.map((id) => {
        const person = contactPeople.find((entry) => Number(entry.id) === Number(id));
        return person ? `${person.first_name} ${person.last_name}`.trim() : '';
      }),
    );
  }, [contactPeople, maxPeople, onContactPersonsChange]);

  // Account standing is a customer-specific concept (credit limit, outstanding
  // balance, utilization, "account in good standing"). It does not apply to
  // suppliers — we don't extend credit to suppliers, and their "balance" is
  // what we owe them, not what they owe us. When the lookup is for suppliers
  // only (purchase documents: expense, DIN, supplier_invoice, PO, etc.), skip
  // the account standing calculation entirely.
  const isSupplierContext = contactTypes.length === 1 && contactTypes[0] === 'supplier';

  const selectedContactStanding = useMemo(() => {
    if (!selectedContact) return null;
    if (isSupplierContext) return null;
    return calculateContactAccountStanding(selectedContact, documentTotal, documentType);
  }, [selectedContact, documentType, documentTotal, isSupplierContext]);

  const closeContactDropdown = useCallback(() => setIsContactOpen(false), []);
  const closeLocationSearch = useCallback(() => setIsLocationSearchOpen(false), []);
  const closePersonSearch = useCallback(() => setIsPersonSearchOpen(false), []);

  // Wrapper that checks supplier compliance status before selecting a contact.
  // Non-compliant suppliers show a warning confirm; restricted suppliers show
  // a danger confirm. The user can proceed or cancel. If they cancel, the
  // contact is not selected.
  const handleContactSelect = useCallback(async (contact: { id: number; company_name: string | null; contact_person: string | null; account_status?: string | null; contact_type?: string }) => {
    const status = contact.account_status;
    const contactName = contact.company_name || contact.contact_person || `Contact ${contact.id}`;

    if (isSupplierRestrictedStatus(status)) {
      const confirmed = await notify.confirm({
        title: 'Restricted Supplier',
        message: `${contactName} is marked as "${formatSupplierStatusLabel(status)}". This supplier is blocked from new purchases pending review. Do you want to proceed with this supplier anyway?`,
        confirmText: 'Proceed with Restricted Supplier',
        cancelText: 'Cancel',
        variant: 'danger',
      });
      if (!confirmed) return;
    } else if (isSupplierWarningStatus(status)) {
      const confirmed = await notify.confirm({
        title: 'Non-Compliant Supplier',
        message: `${contactName} is marked as "${formatSupplierStatusLabel(status)}". This supplier has compliance issues (e.g. expired BEE, insurance, or tax clearance). You can still allocate expenses to them. Do you want to proceed?`,
        confirmText: 'Proceed',
        cancelText: 'Cancel',
        variant: 'warning',
      });
      if (!confirmed) return;
    }

    onContactChange(contact.id, contactName);
    setIsContactOpen(false);
    setContactSearch('');
  }, [onContactChange]);

  const handleEditContact = useCallback((contact: UnifiedLookupContact) => {
    setIsContactOpen(false);
    if (onEditContact) {
      onEditContact(contact);
      return;
    }
    setEditingContact(contact);
  }, [onEditContact]);

  const contactKeyboardNavigation = useKeyboardSelectableList({
    isOpen: isContactOpen,
    items: filteredContacts,
    onSelect: (contact) => {
      void handleContactSelect(contact);
    },
    onClose: closeContactDropdown,
  });

  const locationKeyboardNavigation = useKeyboardSelectableList({
    isOpen: isLocationSearchOpen && locationSearch.length >= 2,
    items: locationSearchResults,
    onSelect: (result) => {
      onUnifiedSelect(result);
      setLocationSearch('');
      setIsLocationSearchOpen(false);
    },
    onClose: closeLocationSearch,
  });

  const personKeyboardNavigation = useKeyboardSelectableList({
    isOpen: isPersonSearchOpen && personSearch.length >= 2,
    items: personSearchResults,
    onSelect: (result) => {
      onUnifiedSelect(result);
      setPersonSearch('');
      setIsPersonSearchOpen(false);
    },
    onClose: closePersonSearch,
  });

  const tabClasses = (active: boolean) => `flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
    active
      ? isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
      : isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
  }`;

  // Fallback display objects for contacts/locations selected via search that
  // may not be in the pre-loaded lists.
  const fallbackContact: UnifiedLookupContact | null = useMemo(() => {
    if (selectedContact) return null;
    if (selectedContactId == null) return null;
    // Build from the last location search result if it matches
    if (lastLocationResult && lastLocationResult.contact_id === selectedContactId) {
      return {
        id: lastLocationResult.contact_id,
        company_name: lastLocationResult.contact_company_name || lastLocationResult.contact_name,
        contact_person: lastLocationResult.contact_name || null,
        email: null,
        phone: null,
        contact_type: lastLocationResult.contact_type || 'customer',
      };
    }
    return null;
  }, [selectedContact, selectedContactId, lastLocationResult]);

  const fallbackLocation: UnifiedLookupLocation | null = useMemo(() => {
    if (selectedLocation) return null;
    if (selectedLocationId == null) return null;
    if (lastLocationResult && lastLocationResult.location_id === selectedLocationId) {
      return {
        id: lastLocationResult.location_id!,
        contact_id: lastLocationResult.contact_id,
        location_name: lastLocationResult.location_name || '',
        address_line1: lastLocationResult.location_address || null,
        address_line2: null,
        city: lastLocationResult.location_city || null,
        state: null,
        postal_code: null,
        country: null,
        contact_person: null,
        contact_email: null,
        contact_phone: null,
        is_primary: false,
      };
    }
    return null;
  }, [selectedLocation, selectedLocationId, lastLocationResult]);

  const displayContact = selectedContact || fallbackContact;
  const displayLocation = selectedLocation || fallbackLocation;

  // Account standing + contact card helpers (skipped for supplier context —
  // see isSupplierContext definition above)
  const accountStandingSnapshot = useMemo(() => {
    if (!displayContact) return null;
    if (isSupplierContext) return null;
    return calculateContactAccountStanding(displayContact, documentTotal, documentType);
  }, [displayContact, documentType, documentTotal, isSupplierContext]);

  const accountStandingTone = accountStandingSnapshot?.currentStanding.status === 'blocked'
    ? 'red'
    : accountStandingSnapshot?.currentStanding.status === 'warning'
      ? 'amber'
      : 'green';

  const normalizePhoneValue = useCallback((value?: string | null) => {
    if (!value) return '';
    return value.replace(/[^\d+]/g, '');
  }, []);

  const buildWhatsAppHref = useCallback((value?: string | null) => {
    const normalized = normalizePhoneValue(value);
    if (!normalized) return null;
    const digits = normalized.startsWith('+') ? normalized.slice(1) : normalized.replace(/^0+/, '');
    return digits ? `https://wa.me/${digits}` : null;
  }, [normalizePhoneValue]);

  const formatCurrencyAmount = useCallback((amount: number, currency = 'ZAR') => {
    return `${currency} ${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)}`;
  }, []);

  const primaryPhone = displayContact?.phone || null;
  const primaryEmail = displayContact?.email || null;
  const whatsappHref = buildWhatsAppHref(primaryPhone);

  const coerceAmount = useCallback((value: number | string | null | undefined): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value.replace(/,/g, ''));
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }, []);

  // Hover-card detail lines for the three summary cards (contact / location /
  // contact person). These surface every field the card itself doesn't show.
  const contactHoverLines = useMemo(() => {
    if (!displayContact) return [] as string[];
    const lines: string[] = [
      `Type: ${displayContact.contact_type}`,
      `Status: ${formatContactAccountStatusLabel(displayContact.account_status, displayContact.is_active ?? true)}`,
    ];
    if (displayContact.contact_person) lines.push(`Contact Person: ${displayContact.contact_person}`);
    if (displayContact.email) lines.push(`Email: ${displayContact.email}`);
    if (displayContact.phone) lines.push(`Phone: ${displayContact.phone}`);
    lines.push('', `Payment Terms: ${formatContactPaymentTerms(displayContact)}`);
    const creditLimit = coerceAmount(displayContact.credit_limit);
    if (creditLimit != null && creditLimit > 0) {
      lines.push(`Credit Limit: ${formatCurrencyAmount(creditLimit, displayContact.currency || 'ZAR')}`);
    }
    const balance = coerceAmount(displayContact.current_balance);
    if (balance != null) {
      lines.push(`Current Balance: ${formatCurrencyAmount(balance, displayContact.currency || 'ZAR')}`);
    }
    if (contactRiskFlags.length > 0) {
      lines.push('', 'Risk Flags:');
      contactRiskFlags.forEach((flag) => lines.push(`• ${flag.title} (${flag.severity})`));
    }
    lines.push('', `Contact ID: ${displayContact.id}`);
    return lines;
  }, [displayContact, contactRiskFlags, coerceAmount, formatCurrencyAmount]);

  const locationHoverLines = useMemo(() => {
    if (!displayLocation) return [] as string[];
    const lines: string[] = [];
    if (displayLocation.is_primary) lines.push('Primary location');
    const addressLines = [
      displayLocation.address_line1,
      displayLocation.address_line2,
      [displayLocation.city, displayLocation.state, displayLocation.postal_code].filter(Boolean).join(', '),
      displayLocation.country,
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    if (addressLines.length > 0) {
      lines.push('', 'Address:');
      lines.push(...addressLines);
    }
    const latitude = coerceAmount(displayLocation.latitude);
    const longitude = coerceAmount(displayLocation.longitude);
    if (latitude != null && longitude != null) {
      lines.push(`GPS: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
    }
    if (displayLocation.contact_person || displayLocation.contact_email || displayLocation.contact_phone) {
      lines.push('', 'Location Contact:');
      if (displayLocation.contact_person) lines.push(displayLocation.contact_person);
      if (displayLocation.contact_email) lines.push(`Email: ${displayLocation.contact_email}`);
      if (displayLocation.contact_phone) lines.push(`Phone: ${displayLocation.contact_phone}`);
    }
    lines.push('', `Location ID: ${displayLocation.id}`);
    return lines;
  }, [displayLocation, coerceAmount]);

  const personHoverLines = useMemo(() => {
    if (!selectedPerson) return [] as string[];
    const lines: string[] = [];
    if (selectedPerson.is_primary) lines.push('Primary contact person');
    if (selectedPerson.job_title) lines.push(`Job Title: ${selectedPerson.job_title}`);
    if (selectedPerson.email) lines.push(`Email: ${selectedPerson.email}`);
    if (selectedPerson.phone) lines.push(`Phone: ${selectedPerson.phone}`);
    if (selectedPerson.mobile) lines.push(`Mobile: ${selectedPerson.mobile}`);
    if (selectedPerson.default_location_name) lines.push('', `Default Location: ${selectedPerson.default_location_name}`);
    if (selectedPerson.contact_company_name) lines.push(`Company: ${selectedPerson.contact_company_name}`);
    lines.push('', `Person ID: ${selectedPerson.id}`);
    if (isMultiPerson && selectedPeople.length > 1) {
      for (const person of selectedPeople.slice(1)) {
        lines.push('', `Also assigned — ${`${person.first_name} ${person.last_name}`.trim()}`);
        if (person.job_title) lines.push(`Job Title: ${person.job_title}`);
        if (person.email) lines.push(`Email: ${person.email}`);
        if (person.mobile || person.phone) lines.push(`Phone: ${person.mobile || person.phone}`);
      }
    }
    return lines;
  }, [selectedPerson, isMultiPerson, selectedPeople]);

  // Combined detail lines for the compact "Contact Card" view — hovering the
  // card surfaces contact, location, and contact person details in one popup.
  const selectedSummaryHoverLines = useMemo(() => {
    const lines = [...contactHoverLines];
    if (displayLocation) {
      lines.push('', `Location — ${displayLocation.location_name}`, ...locationHoverLines);
    }
    if (selectedPerson) {
      const personName = `${selectedPerson.first_name} ${selectedPerson.last_name}`.trim();
      lines.push('', `Contact Person — ${personName}`, ...personHoverLines);
    }
    return lines;
  }, [contactHoverLines, displayLocation, locationHoverLines, selectedPerson, personHoverLines]);

  const handleLocationSearchSelect = (result: UnifiedLookupSearchResult) => {
    // Store the result so fallback display objects can be built
    setLastLocationResult(result);
    // Call specific callbacks so the parent updates contact + location state
    onContactChange(result.contact_id, result.contact_company_name || result.contact_name);
    if (result.location_id != null) {
      onLocationChange(result.location_id);
    }
    onUnifiedSelect(result);
    setLocationSearch('');
    setIsLocationSearchOpen(false);
  };

  const handlePersonSearchSelect = (result: UnifiedLookupSearchResult) => {
    onUnifiedSelect(result);
    setPersonSearch('');
    setIsPersonSearchOpen(false);
  };

  const handleQuickContactCreated = (contact: QuickContactResult) => {
    onContactChange(contact.contact.id, contact.contact.company_name || undefined);

    if (contact.location) {
      const newLocation: UnifiedLookupLocation = {
        id: contact.location.id,
        contact_id: contact.contact.id,
        location_name: contact.location.location_name,
        address_line1: contact.location.address,
        address_line2: null,
        city: null,
        state: null,
        postal_code: null,
        country: null,
        contact_person: null,
        contact_email: null,
        contact_phone: null,
        is_primary: true,
      };
      onLocationCreated(newLocation);
      onLocationChange(newLocation.id);
    }

    if (contact.person) {
      const newPerson: UnifiedLookupContactPerson = {
        id: contact.person.id,
        contact_id: contact.contact.id,
        first_name: contact.person.first_name,
        last_name: contact.person.last_name,
        job_title: contact.person.job_title || null,
        email: contact.person.email || null,
        phone: contact.person.phone || null,
        mobile: null,
        is_primary: true,
        default_location_id: null,
        default_location_name: null,
        contact_company_name: contact.contact.company_name,
      };
      onPersonCreated(newPerson);
      if (isMultiPerson) {
        emitPersonsChange([...selectedPersonIds, newPerson.id]);
      } else {
        onContactPersonChange(newPerson.id, `${newPerson.first_name} ${newPerson.last_name}`);
      }
    }

    setShowQuickContactModal(false);
    onQuickContactCreated?.(contact);
  };

  // Ordered chip list + "add another" select used in multi-person mode —
  // rendered inside the person card and the person-search tab.
  const multiPersonPicker = isMultiPerson ? (
    <div className="space-y-1.5">
      {selectedPersonIds.map((personId, index) => {
        const person = contactPeople.find((entry) => Number(entry.id) === Number(personId));
        const label = person ? `${person.first_name} ${person.last_name}`.trim() : `Person #${personId}`;
        return (
          <div
            key={personId}
            className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs ${
              isDark ? 'bg-purple-900/30 text-purple-200' : 'bg-purple-100 text-purple-800'
            }`}
          >
            <span className="flex-1 min-w-0 truncate">
              {label}
              {index === 0 && (
                <span className={isDark ? 'text-purple-400' : 'text-purple-600'}> (Primary)</span>
              )}
            </span>
            <button
              type="button"
              onClick={() => emitPersonsChange(selectedPersonIds.filter((id) => id !== personId))}
              className="p-0.5 rounded hover:bg-purple-200/60 dark:hover:bg-purple-800/60 transition-colors"
              title={`Remove ${label}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        );
      })}
      {displayContact && selectedPersonIds.length < maxPeople && (
        <select
          value=""
          onChange={(e) => {
            const value = e.target.value;
            if (value) emitPersonsChange([...selectedPersonIds, parseInt(value, 10)]);
          }}
          className={`w-full text-sm py-1 px-2 rounded border ${
            isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
          }`}
        >
          <option value="">
            {selectedPersonIds.length === 0 ? 'Select a contact person...' : 'Add another person...'}
          </option>
          {loadingPeople && <option value="" disabled>Loading people...</option>}
          {filteredContactPeople
            .filter((person) => !selectedPersonIds.includes(Number(person.id)))
            .map((person) => (
              <option key={person.id} value={person.id}>
                {person.first_name} {person.last_name}{person.is_primary ? ' (Primary)' : ''}
              </option>
            ))}
        </select>
      )}
      {displayContact && selectedPersonIds.length >= maxPeople && (
        <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          Maximum of {maxPeople} contact people
        </div>
      )}
      {!displayContact && <div className="text-sm text-gray-400">Select a contact first</div>}
    </div>
  ) : null;

  // Ordered contact-person rows for the compact card view — one row per
  // selected person (index 0 = contact_person_id, then id2, id3). Falls back
  // to the resolved primary person / display name when the people list hasn't
  // hydrated the record yet.
  const cardPersonRows = useMemo(() => {
    const rows = selectedPeople.map((person) => ({
      key: person.id as number | string,
      name: `${person.first_name} ${person.last_name}`.trim(),
      jobTitle: person.job_title,
    }));

    if (rows.length === 0) {
      const fallbackName = selectedPerson
        ? `${selectedPerson.first_name} ${selectedPerson.last_name}`.trim()
        : selectedContactPersonName.trim();
      if (fallbackName) {
        rows.push({
          key: selectedPerson?.id ?? 'primary',
          name: fallbackName,
          jobTitle: selectedPerson?.job_title ?? null,
        });
      }
    }

    return rows;
  }, [selectedPeople, selectedPerson, selectedContactPersonName]);

  return (
    <>
      <div className="space-y-4">
        {/* Toggle between card and search views — only when a contact is selected */}
        {displayContact && !contactOnly && (
          <div className={`flex items-center gap-1 p-1 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <button
              type="button"
              onClick={() => setViewMode('card')}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'card'
                  ? isDark ? 'bg-gray-600 text-white shadow' : 'bg-white text-gray-900 shadow-sm'
                  : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Building2 className="w-3.5 h-3.5" />
              <span>Contact Card</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('search')}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'search'
                  ? isDark ? 'bg-gray-600 text-white shadow' : 'bg-white text-gray-900 shadow-sm'
                  : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Search className="w-3.5 h-3.5" />
              <span>Find / Change</span>
            </button>
          </div>
        )}

        {(contactOnly || !displayContact || viewMode === 'search') && (
        <>
        {!contactOnly && (
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Find by:
          </label>
          <div data-tour-id="contact-lookup-mode-tabs" className={`flex gap-1 p-1 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <button type="button" onClick={() => setSearchMode('contact')} className={tabClasses(searchMode === 'contact')}>
              <Building2 className="w-4 h-4" />
              <span>Contact</span>
            </button>
            <button type="button" onClick={() => setSearchMode('location')} className={tabClasses(searchMode === 'location')}>
              <MapPin className="w-4 h-4" />
              <span>Location</span>
            </button>
            <button type="button" onClick={() => setSearchMode('person')} className={tabClasses(searchMode === 'person')}>
              <User className="w-4 h-4" />
              <span>Contact Person</span>
            </button>
          </div>
        </div>
        )}

        {(contactOnly || searchMode === 'contact') && (
          <div data-tour-id="contact-lookup-contact-field">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {contactLabel}
            </label>
            <div className="relative" ref={contactRef}>
              <button
                type="button"
                onClick={() => setIsContactOpen(!isContactOpen)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border transition-colors ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-gray-200 hover:border-gray-500'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className={`truncate ${!displayContact ? 'text-gray-400' : ''}`}>
                    {displayContact
                      ? (displayContact.company_name || displayContact.contact_person || displayContact.email)
                      : contactPlaceholder}
                  </span>
                </div>
                <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${isContactOpen ? 'rotate-180' : ''}`} />
              </button>

              {isContactOpen && (
                <div className={`absolute z-50 w-full mt-1 rounded-lg shadow-lg border ${
                  isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={contactSearch}
                        onChange={(e) => setContactSearch(e.target.value)}
                        onKeyDown={contactKeyboardNavigation.handleKeyDown}
                        placeholder={contactSearchPlaceholder}
                        className={`w-full pl-8 pr-3 py-1.5 text-sm rounded border ${
                          isDark
                            ? 'bg-gray-700 border-gray-600 text-gray-200'
                            : 'bg-gray-50 border-gray-200 text-gray-700'
                        }`}
                        role="combobox"
                        aria-expanded={isContactOpen}
                        aria-controls="shared-contact-results"
                        aria-activedescendant={contactKeyboardNavigation.activeIndex >= 0 ? `shared-contact-option-${filteredContacts[contactKeyboardNavigation.activeIndex]?.id}` : undefined}
                        autoFocus
                      />
                    </div>
                  </div>

                  <div id="shared-contact-results" role="listbox" className="max-h-48 overflow-y-auto">
                    {loadingContacts || loadingContactSearch ? (
                      <div className="p-3 text-center text-gray-500 text-sm">Loading...</div>
                    ) : filteredContacts.length === 0 ? (
                      <div className="p-3 text-center text-gray-500 text-sm">
                        {contactSearch.trim().length >= 2 ? 'No contacts found' : 'No contacts loaded'}
                      </div>
                    ) : (
                      filteredContacts.map((contact, index) => (
                        <div
                          key={contact.id}
                          onClick={() => {
                            void handleContactSelect(contact);
                          }}
                          ref={contactKeyboardNavigation.registerItemRef(index)}
                          onMouseEnter={() => contactKeyboardNavigation.setActiveIndex(index)}
                          id={`shared-contact-option-${contact.id}`}
                          role="option"
                          aria-selected={contactKeyboardNavigation.activeIndex === index || effectiveSelectedContactId === contact.id}
                          className={`w-full flex items-center gap-2 p-2 text-left transition-colors cursor-pointer ${
                            isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                          } ${effectiveSelectedContactId === contact.id || contactKeyboardNavigation.activeIndex === index ? (isDark ? 'bg-gray-700' : 'bg-blue-50') : ''}`}
                        >
                          <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                              {contact.company_name || contact.contact_person || 'Unnamed'}
                            </div>
                            {contact.email && (
                              <div className="text-xs text-gray-500 truncate">{contact.email}</div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditContact(contact);
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                            title={`Edit ${contact.company_name || contact.contact_person || 'contact'}`}
                            aria-label={`Edit ${contact.company_name || contact.contact_person || 'contact'}`}
                            className={`p-1 rounded transition-colors flex-shrink-0 ${
                              isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-600' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {contact.contact_type}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                  <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsContactOpen(false);
                        setShowQuickContactModal(true);
                      }}
                      className={`w-full flex items-center gap-2 p-2 text-left text-xs font-medium ${isDark ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-600 hover:bg-blue-50'}`}
                    >
                      <Plus className="w-3.5 h-3.5" /> Create New Contact
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {!contactOnly && searchMode === 'location' && (
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Search Locations
            </label>
            <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Search by location name, address, or city. The contact will be auto-selected.
            </p>
            <div className="relative" ref={locationSearchRef}>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={locationSearch}
                  onChange={(e) => {
                    setLocationSearch(e.target.value);
                    setIsLocationSearchOpen(e.target.value.length >= 2);
                  }}
                  onFocus={() => locationSearch.length >= 2 && setIsLocationSearchOpen(true)}
                  onKeyDown={locationKeyboardNavigation.handleKeyDown}
                  placeholder="Search by location name, address, city..."
                  className={`w-full pl-9 pr-3 py-2 rounded-lg border transition-colors ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'
                  } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  role="combobox"
                  aria-expanded={isLocationSearchOpen}
                  aria-controls="shared-location-results"
                  aria-activedescendant={locationKeyboardNavigation.activeIndex >= 0 ? `shared-location-option-${locationKeyboardNavigation.activeIndex}` : undefined}
                />
                {loadingLocationSearch && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
              </div>

              {isLocationSearchOpen && locationSearch.length >= 2 && (
                <div id="shared-location-results" role="listbox" className={`absolute z-50 w-full mt-1 rounded-lg shadow-lg border max-h-80 overflow-y-auto ${
                  isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  {loadingLocationSearch ? (
                    <div className="p-3 text-center text-gray-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" />
                      Searching locations...
                    </div>
                  ) : locationSearchResults.length === 0 ? (
                    <div className="p-3 text-center text-gray-500 text-sm">No locations found</div>
                  ) : (
                    locationSearchResults.map((result, index) => (
                      <button
                        key={`loc-${result.id}-${index}`}
                        type="button"
                        onClick={() => handleLocationSearchSelect(result)}
                        ref={locationKeyboardNavigation.registerItemRef(index)}
                        onMouseEnter={() => locationKeyboardNavigation.setActiveIndex(index)}
                        id={`shared-location-option-${index}`}
                        role="option"
                        aria-selected={locationKeyboardNavigation.activeIndex === index}
                        className={`w-full flex items-start gap-2 p-3 text-left transition-colors border-b last:border-b-0 ${
                          isDark ? 'hover:bg-gray-700 border-gray-700' : 'hover:bg-gray-50 border-gray-100'
                        } ${locationKeyboardNavigation.activeIndex === index ? (isDark ? 'bg-gray-700' : 'bg-blue-50') : ''}`}
                      >
                        <MapPin className="w-4 h-4 text-green-500 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{result.location_name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {result.location_address}
                            {result.location_city && `, ${result.location_city}`}
                          </div>
                          <div className={`text-xs mt-1 flex items-center gap-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                            <Building2 className="w-3 h-3" />
                            {result.contact_company_name || result.contact_name}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {!contactOnly && searchMode === 'person' && (
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Contact Person
            </label>
            {displayContact ? (
              isMultiPerson ? multiPersonPicker : (
              <div className="relative">
                <select
                  value={effectiveSelectedContactPersonId != null ? String(effectiveSelectedContactPersonId) : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) {
                      onContactPersonChange(null, '');
                      return;
                    }
                    const person = filteredContactPeople.find((entry) => String(entry.id) === value);
                    onContactPersonChange(
                      person ? person.id : parseInt(value, 10),
                      person ? `${person.first_name} ${person.last_name}` : '',
                    );
                  }}
                  className={`w-full text-sm py-2 px-3 rounded-lg border ${
                    isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                  } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                >
                  <option value="">Select a contact person...</option>
                  {loadingPeople && <option value="" disabled>Loading people...</option>}
                  {!loadingPeople && filteredContactPeople.length === 0 && (
                    <option value="" disabled>No contact people found for this contact</option>
                  )}
                  {filteredContactPeople.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.first_name} {person.last_name}{person.is_primary ? ' (Primary)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              )
            ) : (
              <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Search by person name, email, or job title. The contact will be auto-selected.
              </p>
            )}
            {!displayContact && (
            <div className="relative" ref={personSearchRef}>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={personSearch}
                  onChange={(e) => {
                    setPersonSearch(e.target.value);
                    setIsPersonSearchOpen(e.target.value.length >= 2);
                  }}
                  onFocus={() => personSearch.length >= 2 && setIsPersonSearchOpen(true)}
                  onKeyDown={personKeyboardNavigation.handleKeyDown}
                  placeholder="Search by name, email, job title..."
                  className={`w-full pl-9 pr-3 py-2 rounded-lg border transition-colors ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'
                  } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  role="combobox"
                  aria-expanded={isPersonSearchOpen}
                  aria-controls="shared-person-results"
                  aria-activedescendant={personKeyboardNavigation.activeIndex >= 0 ? `shared-person-option-${personKeyboardNavigation.activeIndex}` : undefined}
                />
                {loadingPersonSearch && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
              </div>

              {isPersonSearchOpen && personSearch.length >= 2 && (
                <div id="shared-person-results" role="listbox" className={`absolute z-50 w-full mt-1 rounded-lg shadow-lg border max-h-80 overflow-y-auto ${
                  isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  {loadingPersonSearch ? (
                    <div className="p-3 text-center text-gray-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" />
                      Searching people...
                    </div>
                  ) : personSearchResults.length === 0 ? (
                    <div className="p-3 text-center text-gray-500 text-sm">No people found</div>
                  ) : (
                    personSearchResults.map((result, index) => (
                      <button
                        key={`person-${result.id}-${index}`}
                        type="button"
                        onClick={() => handlePersonSearchSelect(result)}
                        ref={personKeyboardNavigation.registerItemRef(index)}
                        onMouseEnter={() => personKeyboardNavigation.setActiveIndex(index)}
                        id={`shared-person-option-${index}`}
                        role="option"
                        aria-selected={personKeyboardNavigation.activeIndex === index}
                        className={`w-full flex items-start gap-2 p-3 text-left transition-colors border-b last:border-b-0 ${
                          isDark ? 'hover:bg-gray-700 border-gray-700' : 'hover:bg-gray-50 border-gray-100'
                        } ${personKeyboardNavigation.activeIndex === index ? (isDark ? 'bg-gray-700' : 'bg-blue-50') : ''}`}
                      >
                        <User className="w-4 h-4 text-purple-500 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{result.person_name}</div>
                          {result.person_job_title && <div className="text-xs text-gray-500 mt-0.5">{result.person_job_title}</div>}
                          {result.person_email && (
                            <div className={`text-xs mt-1 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              <Mail className="w-3 h-3" />
                              {result.person_email}
                            </div>
                          )}
                          <div className={`text-xs mt-1 flex items-center gap-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                            <Building2 className="w-3 h-3" />
                            {result.contact_company_name || result.contact_name}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <DetailHoverCard
            title={displayContact ? (displayContact.company_name || displayContact.contact_person || 'Unnamed') : summaryContactLabel}
            lines={contactHoverLines}
            isDark={isDark}
            disabled={!displayContact}
            className={`p-3 rounded-lg border ${displayContact ? 'cursor-help' : ''} ${
              displayContact
                ? isDark ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'
                : isDark ? 'bg-gray-800/50 border-gray-700 border-dashed' : 'bg-gray-50 border-gray-300 border-dashed'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                displayContact ? (isDark ? 'bg-blue-600' : 'bg-blue-500') : isDark ? 'bg-gray-700' : 'bg-gray-200'
              }`}>
                <Building2 className={`w-4 h-4 ${displayContact ? 'text-white' : 'text-gray-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{summaryContactLabel}</div>
                {displayContact ? (
                  <div className={`text-sm font-semibold truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {displayContact.company_name || displayContact.contact_person}
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">Not selected</div>
                )}
              </div>
              {displayContact && (
                <button type="button" onClick={() => { onContactChange(null); setLastLocationResult(null); }} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>
            {displayContact && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full ${isDark ? 'bg-blue-800 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                    {displayContact.contact_type}
                  </span>
                  {contactRiskFlags.length > 0 && contactRiskFlags.map((flag) => {
                    const isHigh = flag.severity === 'high' || flag.severity === 'critical';
                    return (
                      <span
                        key={flag.id}
                        title={flag.title}
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                          isHigh
                            ? (isDark ? 'bg-red-900/60 text-red-300' : 'bg-red-100 text-red-700')
                            : (isDark ? 'bg-amber-900/60 text-amber-300' : 'bg-amber-100 text-amber-700')
                        }`}
                      >
                        <ShieldAlert className="w-3 h-3" />
                        {flag.title}
                      </span>
                    );
                  })}
                  {displayContact.email && <span className={`truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{displayContact.email}</span>}
                </div>
                {selectedContactStanding && (
                  <div className="space-y-1.5">
                  <div className="flex items-center justify-end gap-2 flex-wrap">
                      {selectedContactStanding.creditLimit > 0 && (
                        <span className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {(displayContact?.currency || 'ZAR')} {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(selectedContactStanding.currentBalance)} / {(displayContact?.currency || 'ZAR')} {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(selectedContactStanding.creditLimit)}
                        </span>
                      )}
                    </div>
                    {selectedContactStanding.currentStanding.status !== 'good' && (
                      <div className={`p-1.5 rounded-lg border text-[11px] flex items-start gap-1.5 ${
                        selectedContactStanding.currentStanding.status === 'blocked'
                          ? 'bg-red-50 border-red-300 text-red-800'
                          : 'bg-yellow-50 border-yellow-300 text-yellow-800'
                      }`}>
                        <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-semibold">{selectedContactStanding.currentStanding.message}: </span>
                          {selectedContactStanding.currentStanding.reasons[0]}
                          {selectedContactStanding.currentStanding.reasons.length > 1 && (
                            <span className="opacity-75"> +{selectedContactStanding.currentStanding.reasons.length - 1} more</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </DetailHoverCard>

          <DetailHoverCard
            title={displayLocation ? displayLocation.location_name : locationLabel}
            lines={locationHoverLines}
            isDark={isDark}
            disabled={!displayLocation}
            className={`p-3 rounded-lg border ${displayLocation ? 'cursor-help' : ''} ${
              displayLocation
                ? isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'
                : isDark ? 'bg-gray-800/50 border-gray-700 border-dashed' : 'bg-gray-50 border-gray-300 border-dashed'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                displayLocation ? (isDark ? 'bg-green-600' : 'bg-green-500') : isDark ? 'bg-gray-700' : 'bg-gray-200'
              }`}>
                <MapPin className={`w-4 h-4 ${displayLocation ? 'text-white' : 'text-gray-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{locationLabel}</div>
                {displayLocation ? (
                  <div className={`text-sm font-semibold truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{displayLocation.location_name}</div>
                ) : displayContact ? (
                  <select
                    value={effectiveSelectedLocationId ?? ''}
                    onChange={(e) => onLocationChange(e.target.value ? parseInt(e.target.value, 10) : null)}
                    className={`w-full text-sm py-1 px-2 rounded border ${
                      isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  >
                    <option value="">Select location...</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.location_name}{location.is_primary ? ' (Primary)' : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-sm text-gray-400">Select contact first</div>
                )}
              </div>
              {displayLocation && (
                <button type="button" onClick={() => onLocationChange(null)} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>
            {displayLocation && (
              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <div className="truncate">{displayLocation.address_line1}</div>
                <div className="truncate">{[displayLocation.city, displayLocation.postal_code].filter(Boolean).join(', ')}</div>
              </div>
            )}
            {loadingLocations && (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Loader2 className="w-3 h-3 animate-spin" />
                Loading...
              </div>
            )}
            {displayContact && !displayLocation && (
              <button
                type="button"
                onClick={() => setShowAddLocation(true)}
                className={`mt-2 flex items-center gap-1 text-xs font-medium px-2 py-1 rounded transition-colors ${
                  isDark ? 'text-green-400 hover:bg-green-900/30' : 'text-green-600 hover:bg-green-50'
                }`}
              >
                <Plus className="w-3 h-3" />
                New Location
              </button>
            )}
          </DetailHoverCard>

          <DetailHoverCard
            title={selectedPerson ? `${selectedPerson.first_name} ${selectedPerson.last_name}`.trim() : personLabel}
            lines={personHoverLines}
            isDark={isDark}
            disabled={!selectedPerson}
            className={`p-3 rounded-lg border ${selectedPerson ? 'cursor-help' : ''} ${
              selectedPerson
                ? isDark ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'
                : isDark ? 'bg-gray-800/50 border-gray-700 border-dashed' : 'bg-gray-50 border-gray-300 border-dashed'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                (selectedPerson || selectedPersonIds.length > 0) ? (isDark ? 'bg-purple-600' : 'bg-purple-500') : isDark ? 'bg-gray-700' : 'bg-gray-200'
              }`}>
                <User className={`w-4 h-4 ${(selectedPerson || selectedPersonIds.length > 0) ? 'text-white' : 'text-gray-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {personLabel}{isMultiPerson ? ` (up to ${maxPeople})` : ''}
                </div>
                {isMultiPerson ? (
                  <div className={`text-sm ${selectedPersonIds.length > 0 ? `font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}` : 'text-gray-400'}`}>
                    {selectedPersonIds.length > 0 ? `${selectedPersonIds.length} selected` : 'Optional'}
                  </div>
                ) : selectedPerson ? (
                  <div className={`text-sm font-semibold truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {selectedPerson.first_name} {selectedPerson.last_name}
                  </div>
                ) : displayContact ? (
                  <select
                    value={effectiveSelectedContactPersonId != null ? String(effectiveSelectedContactPersonId) : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) {
                        onContactPersonChange(null, '');
                        return;
                      }
                      const person = filteredContactPeople.find((entry) => String(entry.id) === value);
                      onContactPersonChange(
                        person ? person.id : parseInt(value, 10),
                        person ? `${person.first_name} ${person.last_name}` : '',
                      );
                    }}
                    className={`w-full text-sm py-1 px-2 rounded border ${
                      isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  >
                    <option value="">Optional...</option>
                    {filteredContactPeople.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.first_name} {person.last_name}{person.is_primary ? ' (Primary)' : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-sm text-gray-400">Optional</div>
                )}
              </div>
              {selectedPerson && !isMultiPerson && (
                <button type="button" onClick={() => onContactPersonChange(null, '')} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>
            {multiPersonPicker}
            {!isMultiPerson && selectedPerson && (
              <div className={`text-xs space-y-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {selectedPerson.job_title && <div className="truncate">{selectedPerson.job_title}</div>}
                {selectedPerson.email && (
                  <div className="flex items-center gap-1 truncate">
                    <Mail className="w-3 h-3" />
                    {selectedPerson.email}
                  </div>
                )}
                {(selectedPerson.mobile || selectedPerson.phone) && (
                  <div className="flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {selectedPerson.mobile || selectedPerson.phone}
                  </div>
                )}
              </div>
            )}
            {loadingPeople && (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Loader2 className="w-3 h-3 animate-spin" />
                Loading...
              </div>
            )}
            {selectedContact && (isMultiPerson ? selectedPersonIds.length < maxPeople : !selectedPerson) && (
              <button
                type="button"
                onClick={() => setShowAddPerson(true)}
                className={`mt-2 flex items-center gap-1 text-xs font-medium px-2 py-1 rounded transition-colors ${
                  isDark ? 'text-purple-400 hover:bg-purple-900/30' : 'text-purple-600 hover:bg-purple-50'
                }`}
              >
                <Plus className="w-3 h-3" />
                New Person
              </button>
            )}
          </DetailHoverCard>
        </div>
        </>
        )}

        {/* Contact Card — shown in card view when a contact is selected */}
        {!contactOnly && displayContact && viewMode === 'card' && (
          <div
            className={`mt-2 rounded-xl border p-2.5 ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-white'}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Building2 className={`w-3.5 h-3.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <div className={`text-xs font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    {displayContact.company_name || displayContact.contact_person || 'Unnamed'}
                  </div>
                  {accountStandingSnapshot && (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${accountStandingTone === 'red'
                        ? (isDark ? 'bg-red-900/40 text-red-200 ring-1 ring-red-700/70' : 'bg-red-100 text-red-800 ring-1 ring-red-200')
                        : accountStandingTone === 'amber'
                          ? (isDark ? 'bg-amber-900/40 text-amber-200 ring-1 ring-amber-700/70' : 'bg-amber-100 text-amber-800 ring-1 ring-amber-200')
                          : (isDark ? 'bg-emerald-900/40 text-emerald-200 ring-1 ring-emerald-700/70' : 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200')
                        }`}
                    >
                      {accountStandingTone === 'red' ? <ShieldAlert className="w-3 h-3" /> : accountStandingTone === 'amber' ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                      {accountStandingSnapshot.currentStanding.message}
                    </span>
                  )}
                </div>
                {cardPersonRows.length > 0 && (
                  <div className="mt-0.5 space-y-0.5">
                    {cardPersonRows.map((person, index) => (
                      <div key={person.key} className={`flex items-center gap-1.5 text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <User className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-purple-400' : 'text-purple-500'}`} />
                        <span className="truncate">
                          {person.name}
                          {index === 0 && !isMultiPerson && person.jobTitle ? <span className="text-gray-500"> · {person.jobTitle}</span> : null}
                        </span>
                        {index === 0 && (
                          <button type="button" onClick={() => setViewMode('search')} className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}>
                            <Pencil className="w-2.5 h-2.5" />Change
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {cardPersonRows.length === 0 && displayContact && (
                  <button type="button" onClick={() => setViewMode('search')} className={`mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}>
                    <Pencil className="w-2.5 h-2.5" />Select person
                  </button>
                )}
                {displayLocation && (
                  <div className={`mt-1.5 flex items-start gap-1.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>{[displayLocation.location_name, displayLocation.address_line1, displayLocation.city].filter(Boolean).join(', ')}</span>
                    <button type="button" onClick={() => setViewMode('search')} className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}>
                      <Pencil className="w-2.5 h-2.5" />Change
                    </button>
                  </div>
                )}
                {!displayLocation && displayContact && (
                  <button type="button" onClick={() => setViewMode('search')} className={`mt-1.5 inline-flex items-center gap-0.5 text-[10px] font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}>
                    <Pencil className="w-2.5 h-2.5" />Select location
                  </button>
                )}
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <DetailHoverCard
                  as="span"
                  title={displayContact.company_name || displayContact.contact_person || 'Unnamed'}
                  lines={selectedSummaryHoverLines}
                  isDark={isDark}
                  className={`p-1 rounded-lg inline-flex cursor-help transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                >
                  <Info className="w-3.5 h-3.5 text-gray-500" />
                </DetailHoverCard>
                {primaryPhone && (
                  <a
                    href={`tel:${normalizePhoneValue(primaryPhone)}`}
                    className={`p-1 rounded-lg transition-colors ${isDark ? 'bg-green-900/30 hover:bg-green-900/50' : 'bg-green-50 hover:bg-green-100'}`}
                    title="Call contact"
                  >
                    <Phone className="w-3.5 h-3.5 text-green-600" />
                  </a>
                )}
                {whatsappHref && (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-1 rounded-lg transition-colors ${isDark ? 'bg-emerald-900/30 hover:bg-emerald-900/50' : 'bg-emerald-50 hover:bg-emerald-100'}`}
                    title="WhatsApp contact"
                  >
                    <MessageCircle className="w-3.5 h-3.5 text-emerald-500" />
                  </a>
                )}
                {primaryEmail && (
                  <a
                    href={`mailto:${primaryEmail}`}
                    className={`p-1 rounded-lg transition-colors ${isDark ? 'bg-blue-900/30 hover:bg-blue-900/50' : 'bg-blue-50 hover:bg-blue-100'}`}
                    title="Email contact"
                  >
                    <Mail className="w-3.5 h-3.5 text-blue-600" />
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => handleEditContact(displayContact)}
                  className={`p-1 rounded-lg transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                  title="Edit contact"
                >
                  <Pencil className="w-3.5 h-3.5 text-gray-500" />
                </button>
                {onViewStakeholder && (
                  <button
                    type="button"
                    onClick={onViewStakeholder}
                    className={`p-1 rounded-lg transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                    title="View stakeholder overview"
                  >
                    <ExternalLink className="w-3.5 h-3.5 text-gray-500" />
                  </button>
                )}
                {onOnboardStakeholder && (
                  <button
                    type="button"
                    onClick={onOnboardStakeholder}
                    className={`p-1 rounded-lg transition-colors ${isDark ? 'bg-indigo-900/30 hover:bg-indigo-900/50' : 'bg-indigo-50 hover:bg-indigo-100'}`}
                    title="Onboard new stakeholder"
                  >
                    <UserPlus className="w-3.5 h-3.5 text-indigo-500" />
                  </button>
                )}
              </div>
            </div>

            {accountStandingSnapshot && (
              <div
                className={`mt-2 rounded-xl border px-2.5 py-2 ${accountStandingTone === 'red'
                  ? (isDark ? 'border-red-800 bg-red-950/30' : 'border-red-200 bg-red-50')
                  : accountStandingTone === 'amber'
                    ? (isDark ? 'border-amber-800 bg-amber-950/20' : 'border-amber-200 bg-amber-50')
                    : (isDark ? 'border-emerald-800 bg-emerald-950/20' : 'border-emerald-200 bg-emerald-50')
                  }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className={`flex items-center gap-2 text-xs font-semibold ${accountStandingTone === 'red'
                      ? (isDark ? 'text-red-200' : 'text-red-800')
                      : accountStandingTone === 'amber'
                        ? (isDark ? 'text-amber-200' : 'text-amber-800')
                        : (isDark ? 'text-emerald-200' : 'text-emerald-800')
                      }`}>
                      {accountStandingTone === 'red' ? <ShieldAlert className="w-3.5 h-3.5" /> : accountStandingTone === 'amber' ? <AlertTriangle className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                      Account standing
                    </div>
                    <div className={`mt-0.5 text-base font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {accountStandingSnapshot.currentStanding.message}
                    </div>
                    <div className={`mt-0.5 text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {formatContactAccountStatusLabel(displayContact.account_status, displayContact.is_active ?? true)} · {formatContactPaymentTerms(displayContact)}
                    </div>
                  </div>
                  <div className="grid min-w-[200px] gap-1.5 sm:grid-cols-2">
                    <div className={`rounded-lg px-2.5 py-1.5 ${isDark ? 'bg-black/20' : 'bg-white/70'}`}>
                      <div className={`text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Balance</div>
                      <div className={`mt-0.5 text-xs font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        {formatCurrencyAmount(Number(accountStandingSnapshot.currentBalance), accountStandingSnapshot.currency)}
                      </div>
                    </div>
                    <div className={`rounded-lg px-2.5 py-1.5 ${isDark ? 'bg-black/20' : 'bg-white/70'}`}>
                      <div className={`text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Credit limit</div>
                      <div className={`mt-0.5 text-xs font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        {Number(accountStandingSnapshot.creditLimit) > 0
                          ? formatCurrencyAmount(Number(accountStandingSnapshot.creditLimit), accountStandingSnapshot.currency)
                          : 'Not set'}
                      </div>
                    </div>
                    <div className={`rounded-lg px-2.5 py-1.5 ${isDark ? 'bg-black/20' : 'bg-white/70'}`}>
                      <div className={`text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Available credit</div>
                      <div className={`mt-0.5 text-xs font-semibold ${accountStandingSnapshot.availableCredit < 0
                        ? (isDark ? 'text-red-300' : 'text-red-700')
                        : isDark ? 'text-gray-100' : 'text-gray-900'
                        }`}>
                        {Number(accountStandingSnapshot.creditLimit) > 0
                          ? formatCurrencyAmount(Number(accountStandingSnapshot.availableCredit), accountStandingSnapshot.currency)
                          : 'Unlimited / n.a.'}
                      </div>
                    </div>
                    <div className={`rounded-lg px-2.5 py-1.5 ${isDark ? 'bg-black/20' : 'bg-white/70'}`}>
                      <div className={`text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Utilization</div>
                      <div className={`mt-0.5 text-xs font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        {accountStandingSnapshot.currentUtilization != null
                          ? `${accountStandingSnapshot.currentUtilization.toFixed(0)}%`
                          : 'n.a.'}
                      </div>
                    </div>
                  </div>
                </div>

                {accountStandingSnapshot.currentStanding.reasons.length > 0 && (
                  <div className={`mt-2 rounded-lg px-2.5 py-1.5 text-xs ${isDark ? 'bg-black/20 text-gray-200' : 'bg-white/70 text-gray-800'}`}>
                    <div className="font-semibold">Why this matters</div>
                    <ul className="mt-0.5 space-y-0.5">
                      {accountStandingSnapshot.currentStanding.reasons.slice(0, 2).map((reason) => (
                        <li key={reason} className="flex items-start gap-2">
                          <span className="mt-0.5">•</span>
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Supplier info card — shown for supplier-only lookups (purchase
                documents) instead of the customer account standing card.
                Shows the supplier compliance status (preferred, approved,
                non-compliant, restricted) and payment terms. */}
            {isSupplierContext && displayContact && (() => {
              const supplierStatus = displayContact.account_status;
              const isRestricted = isSupplierRestrictedStatus(supplierStatus);
              const isWarning = isSupplierWarningStatus(supplierStatus);
              const isPreferred = supplierStatus === 'preferred_supplier';
              const isApproved = supplierStatus === 'approved_supplier';
              const statusLabel = isSupplierStatus(supplierStatus)
                ? formatSupplierStatusLabel(supplierStatus)
                : displayContact.is_active === false
                  ? 'Inactive'
                  : 'Active';
              const badgeClass = isRestricted
                ? (isDark ? 'bg-red-900/40 text-red-200 ring-1 ring-red-700/70' : 'bg-red-100 text-red-800 ring-1 ring-red-200')
                : isWarning
                  ? (isDark ? 'bg-amber-900/40 text-amber-200 ring-1 ring-amber-700/70' : 'bg-amber-100 text-amber-800 ring-1 ring-amber-200')
                  : isPreferred
                    ? (isDark ? 'bg-blue-900/40 text-blue-200 ring-1 ring-blue-700/70' : 'bg-blue-100 text-blue-800 ring-1 ring-blue-200')
                    : isApproved
                      ? (isDark ? 'bg-emerald-900/40 text-emerald-200 ring-1 ring-emerald-700/70' : 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200')
                      : displayContact.is_active === false
                        ? (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')
                        : (isDark ? 'bg-emerald-900/40 text-emerald-200' : 'bg-emerald-100 text-emerald-800');
              return (
                <div className={`mt-2 rounded-xl border px-2.5 py-2 ${
                  isRestricted
                    ? (isDark ? 'border-red-800 bg-red-950/20' : 'border-red-200 bg-red-50')
                    : isWarning
                      ? (isDark ? 'border-amber-800 bg-amber-950/20' : 'border-amber-200 bg-amber-50')
                      : (isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50')
                }`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Building2 className={`w-3.5 h-3.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className={`text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      Supplier
                    </span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${badgeClass}`}>
                      {isRestricted ? <ShieldAlert className="w-3 h-3" /> : isWarning ? <AlertTriangle className="w-3 h-3" /> : isPreferred ? <Star className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                      {statusLabel}
                    </span>
                    <span className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      · {formatContactPaymentTerms(displayContact)}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {showAddLocation && selectedContact && (
          <div className={`p-4 rounded-lg border-2 ${isDark ? 'bg-green-900/10 border-green-700' : 'bg-green-50 border-green-300'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MapPin className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                <h4 className={`text-sm font-semibold ${isDark ? 'text-green-300' : 'text-green-800'}`}>Add New Location</h4>
              </div>
              <button type="button" onClick={() => setShowAddLocation(false)} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Location Name *</label>
                <input
                  type="text"
                  value={newLocation.location_name}
                  onChange={(e) => setNewLocation((prev) => ({ ...prev, location_name: e.target.value }))}
                  placeholder="e.g. Main Office, Warehouse A"
                  className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:ring-2 focus:ring-green-500`}
                />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Location Type *</label>
                <select
                  value={newLocation.location_type}
                  onChange={(e) => setNewLocation((prev) => ({ ...prev, location_type: e.target.value }))}
                  className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:ring-2 focus:ring-green-500`}
                >
                  {locationTypes.map((locationType) => (
                    <option key={locationType} value={locationType}>
                      {locationType.charAt(0).toUpperCase() + locationType.slice(1).replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={`p-3 rounded-lg border mb-3 ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-3 h-3 text-gray-400" />
                <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Address (auto-filled from map)</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <input type="text" value={newLocation.address_line1} readOnly placeholder="Select location on map below" className={`w-full px-2 py-1.5 text-xs rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-200 text-gray-600'} cursor-not-allowed`} />
                </div>
                <input type="text" value={newLocation.city} readOnly placeholder="City" className={`w-full px-2 py-1.5 text-xs rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-200 text-gray-600'} cursor-not-allowed`} />
                <input type="text" value={newLocation.state} readOnly placeholder="Province" className={`w-full px-2 py-1.5 text-xs rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-200 text-gray-600'} cursor-not-allowed`} />
                <input type="text" value={newLocation.postal_code} readOnly placeholder="Postal Code" className={`w-full px-2 py-1.5 text-xs rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-200 text-gray-600'} cursor-not-allowed`} />
                <input type="text" value={newLocation.country} readOnly className={`w-full px-2 py-1.5 text-xs rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-200 text-gray-600'} cursor-not-allowed`} />
              </div>
            </div>

            <div className={`flex items-center gap-2 mb-2 text-xs ${
              newLocation.latitude && newLocation.longitude
                ? isDark ? 'text-green-400' : 'text-green-600'
                : isDark ? 'text-amber-400' : 'text-amber-600'
            }`}>
              <MapPin className="w-3 h-3" />
              {newLocation.latitude && newLocation.longitude ? (
                <span>GPS: {newLocation.latitude.toFixed(6)}, {newLocation.longitude.toFixed(6)} (Radius: {newLocation.radius_meters}m)</span>
              ) : (
                <span>Search or click on the map to set the location coordinates</span>
              )}
            </div>

            <div className="h-56 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 mb-3">
              <GoogleMapsPicker
                onLocationSelect={(lat: string, lng: string, address?: string) => handleMapLocationSelect(lat, lng, address)}
                initialLat={newLocation.latitude?.toString() || '-25.8323022'}
                initialLng={newLocation.longitude?.toString() || '28.2105906'}
                showSearch={true}
              />
            </div>

            <div className="mb-3">
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Geofence Radius (meters)</label>
              <input
                type="number"
                value={newLocation.radius_meters}
                onChange={(e) => setNewLocation((prev) => ({ ...prev, radius_meters: parseInt(e.target.value, 10) || 100 }))}
                className={`w-24 px-3 py-1.5 text-sm rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
                min={10}
                max={10000}
              />
            </div>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowAddLocation(false)} className={`px-3 py-1.5 text-xs font-medium rounded-lg ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}>Cancel</button>
              <button type="button" onClick={handleSaveNewLocation} disabled={savingLocation} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50">
                {savingLocation ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Create Location
              </button>
            </div>
          </div>
        )}

        {showAddPerson && selectedContact && (
          <div className={`p-4 rounded-lg border-2 ${isDark ? 'bg-purple-900/10 border-purple-700' : 'bg-purple-50 border-purple-300'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <User className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                <h4 className={`text-sm font-semibold ${isDark ? 'text-purple-300' : 'text-purple-800'}`}>Add New Contact Person</h4>
              </div>
              <button type="button" onClick={() => setShowAddPerson(false)} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>First Name *</label>
                <input type="text" value={newPerson.first_name} onChange={(e) => setNewPerson((prev) => ({ ...prev, first_name: e.target.value }))} className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:ring-2 focus:ring-purple-500`} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Last Name *</label>
                <input type="text" value={newPerson.last_name} onChange={(e) => setNewPerson((prev) => ({ ...prev, last_name: e.target.value }))} className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:ring-2 focus:ring-purple-500`} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Job Title</label>
                <input type="text" value={newPerson.job_title} onChange={(e) => setNewPerson((prev) => ({ ...prev, job_title: e.target.value }))} placeholder="e.g. Site Manager" className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:ring-2 focus:ring-purple-500`} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email</label>
                <input type="email" value={newPerson.email} onChange={(e) => setNewPerson((prev) => ({ ...prev, email: e.target.value }))} className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:ring-2 focus:ring-purple-500`} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Phone</label>
                <input type="tel" value={newPerson.phone} onChange={(e) => setNewPerson((prev) => ({ ...prev, phone: e.target.value }))} className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:ring-2 focus:ring-purple-500`} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Mobile</label>
                <input type="tel" value={newPerson.mobile} onChange={(e) => setNewPerson((prev) => ({ ...prev, mobile: e.target.value }))} className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:ring-2 focus:ring-purple-500`} />
              </div>
            </div>

            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <input type="checkbox" checked={newPerson.is_primary} onChange={(e) => setNewPerson((prev) => ({ ...prev, is_primary: e.target.checked }))} className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
              <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Primary Contact Person</span>
            </label>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowAddPerson(false)} className={`px-3 py-1.5 text-xs font-medium rounded-lg ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}>Cancel</button>
              <button type="button" onClick={handleSaveNewPerson} disabled={savingPerson} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50">
                {savingPerson ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Create Person
              </button>
            </div>
          </div>
        )}
      </div>

      <QuickContactModal
        isOpen={showQuickContactModal}
        onClose={() => setShowQuickContactModal(false)}
        isDark={isDark}
        onCreated={handleQuickContactCreated}
        defaultContactType={isSupplierContext ? 'supplier' : 'customer'}
        contactTypes={contactTypes as Array<'customer' | 'vendor' | 'supplier' | 'partner' | 'other'>}
      />

      {!onEditContact && editingContact && (
        <WorkItemDetailModal
          isOpen={true}
          onClose={() => setEditingContact(null)}
          workItem={buildStakeholderContactWorkItem(editingContact as Parameters<typeof buildStakeholderContactWorkItem>[0])}
          visibleTabs={['overview', 'stakeholder_people', 'stakeholder_locations', 'stakeholder_documents', 'stakeholder_hierarchy', 'stakeholder_time_tracking', 'stakeholder_assets', 'history']}
          initialTab="overview"
          containerZIndex={140}
        />
      )}
    </>
  );
}
